import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Play, Save, Square, RefreshCw } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Progress } from "./ui/progress";

const API = process.env.REACT_APP_BACKEND_URL;
const FIELD_LABELS = {
  filing_date: "Ημερομηνία Κατάθεσης", eak_number: "ΕΑΚ", procedure_type: "Διαδικασία", subject: "Αντικείμενο", case_type: "Είδος Δικογράφου",
  withdrawal_indicator: "Παραίτηση", hearing_date: "Ημερομηνία Πινακίου", solon_pinakio_number: "Αριθμός Πινακίου",
  solon_hearing_result: "Αποτέλεσμα Συζήτησης", solon_decision: "Αριθμός Απόφασης", solon_decision_date: "Ημ. Δημοσίευσης Απόφασης", solon_bench_judge: "Δικαστής (SOLON)",
  order_issued: "Έκδοση Διάταξης", order_issue_date: "Ημ. Διάταξης", mediation_start: "Υπαγωγή σε Διαμεσολάβηση (έναρξη)",
  mediation_result: "Πρακτικό Επιτυχούς Διαμεσολάβησης", mediation_end: "Λήξη Διαμεσολάβησης",
};
const ALL = "__all__";

/** Admin → Εργαλεία: μαζικός έλεγχος στο SOLON.GOV ανά τύπο δικαστηρίου + μαζική ενημέρωση στοιχείων. */
const SolonBulkCard = () => {
  const [courtType, setCourtType] = useState(ALL);
  const [year, setYear] = useState("");
  const [onlyUnchecked, setOnlyUnchecked] = useState(true);
  const [limit, setLimit] = useState(100);
  const [job, setJob] = useState(null);
  const [summary, setSummary] = useState(null);
  const [fields, setFields] = useState({});
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState(null);
  const [offices, setOffices] = useState([]);
  const [auto, setAuto] = useState(null);           // ρυθμίσεις αυτόματης συνέχισης (από bulk-status)
  const [autoPause, setAutoPause] = useState(30);
  const [autoFields, setAutoFields] = useState({});

  const params = () => ({ court_type: courtType === ALL ? undefined : courtType, gak_year: year || undefined });

  const loadStatus = async () => {
    try {
      const [s, sm, st] = await Promise.all([
        axios.get(`${API}/api/admin/solon/bulk-status`),
        axios.get(`${API}/api/admin/solon/bulk-summary`, { params: params() }),
        axios.get(`${API}/api/solon/settings`),
      ]);
      setJob(s.data.job); setSummary(sm.data); setSettings(st.data);
      if (s.data.auto_continue) {
        setAuto(s.data.auto_continue);
        if (!autoTouched) {
          setAutoPause(s.data.auto_continue.pause_seconds || 30);
          const f = {}; (s.data.auto_continue.apply_fields || []).forEach((k) => { f[k] = true; }); setAutoFields(f);
        }
      }
    } catch (e) { /* silent */ }
  };
  const [autoTouched, setAutoTouched] = useState(false);
  const saveAuto = async (enabled) => {
    const lim = Math.max(1, Math.min(parseInt(limit, 10) || 100, 1000));
    setBusy(true);
    try {
      const r = await axios.put(`${API}/api/admin/solon/auto-continue`, {
        enabled, court_type: courtType === ALL ? null : courtType, gak_year: year || null, limit: lim,
        pause_seconds: Math.max(5, parseInt(autoPause, 10) || 30), apply_fields: Object.keys(autoFields).filter((k) => autoFields[k]),
      });
      setAuto(r.data.auto_continue); setAutoTouched(false);
      toast.success(enabled ? `Αυτόματη συνέχιση ΕΝΕΡΓΗ — απομένουν ${r.data.auto_continue.remaining} υποθέσεις χωρίς έλεγχο` : "Η αυτόματη συνέχιση απενεργοποιήθηκε");
      loadStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα"); }
    finally { setBusy(false); }
  };
  useEffect(() => { loadStatus(); }, [courtType, year]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const active = job?.status === "running" || job?.status === "queued" || auto?.enabled;
    if (!active) return;
    const t = setInterval(loadStatus, auto?.enabled && job?.status !== "running" ? 10000 : 2500);
    return () => clearInterval(t);
  }, [job?.status, auto?.enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    const lim = Math.max(1, Math.min(parseInt(limit, 10) || 100, 1000));
    setLimit(lim);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/admin/solon/bulk-check`, { ...params(), only_unchecked: onlyUnchecked, limit: lim });
      setJob(r.data); toast.success(`Ο μαζικός έλεγχος ξεκίνησε (έως ${lim} υποθέσεις)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα εκκίνησης"); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    try {
      const r = await axios.post(`${API}/api/admin/solon/bulk-cancel`);
      toast.success(r.data?.cancelled ? "Ο μαζικός έλεγχος διακόπηκε" : "Δεν υπάρχει έλεγχος σε εξέλιξη");
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα διακοπής"); }
    loadStatus();
  };
  const applyAll = async () => {
    const sel = Object.keys(fields).filter((k) => fields[k]);
    if (!sel.length) return toast.error("Επιλέξτε πεδία προς ενημέρωση");
    if (!window.confirm(`Μαζική ενημέρωση ${sel.length} πεδίων σε όλες τις ελεγμένες υποθέσεις${courtType !== ALL ? ` (${courtType})` : ""}. Συνέχεια;`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/admin/solon/bulk-apply`, { ...params(), fields: sel });
      toast.success(`Ενημερώθηκαν ${r.data.updated_cases} υποθέσεις`); loadStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα"); }
    finally { setBusy(false); }
  };
  const loadOffices = async () => {
    try { const r = await axios.get(`${API}/api/admin/solon/court-offices`); setOffices(r.data.offices); }
    catch (e) { toast.error(e.response?.data?.detail || "Αδυναμία φόρτωσης Καταστημάτων"); }
  };
  const saveOffice = async (code) => {
    const o = offices.find((x) => x.code === code); if (!o) return;
    const r = await axios.put(`${API}/api/admin/solon/settings`, { court_office_code: o.code, court_office_label: o.label });
    setSettings(r.data); toast.success(`Κατάστημα SOLON: ${o.label}`);
  };

  const running = job?.status === "running" || job?.status === "queued";
  const pct = job?.total ? Math.round((job.done / job.total) * 100) : 0;
  const changed = summary?.changed_per_field || {};

  return (
    <Card data-testid="solon-bulk-card">
      <CardHeader>
        <CardTitle className="text-base">SOLON.GOV — Μαζικός έλεγχος &amp; ενημέρωση υποθέσεων</CardTitle>
        <CardDescription>Άντληση στοιχείων από την «Πορεία Υπόθεσης (Πολιτική)» του extapps.solon.gov.gr για όλες τις υποθέσεις ανά τύπο δικαστηρίου. Οι έλεγχοι τρέχουν στο παρασκήνιο (≈3–5″ ανά υπόθεση) <b>και συνεχίζονται ακόμη κι όταν ο διαχειριστής δεν είναι συνδεδεμένος</b>: ο διακομιστής ελέγχει κάθε 15′ και επανεκκινεί διακοπέντες ελέγχους ή την αυτόματη συνέχιση.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Κατάστημα SOLON</Label>
            <div className="flex gap-2">
              <Select value={settings?.court_office_code || ""} onValueChange={saveOffice} onOpenChange={(o) => o && !offices.length && loadOffices()}>
                <SelectTrigger className="w-72 h-9" data-testid="solon-office-select"><SelectValue placeholder={settings?.court_office_label || "…"}>{settings?.court_office_label}</SelectValue></SelectTrigger>
                <SelectContent className="max-h-72">
                  {!offices.length && <div className="px-3 py-2 text-xs text-slate-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" />Φόρτωση από SOLON…</div>}
                  {offices.map((o) => <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Τύπος Δικαστηρίου</Label>
            <Select value={courtType} onValueChange={setCourtType}>
              <SelectTrigger className="w-44 h-9" data-testid="solon-court-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={ALL}>Όλα</SelectItem><SelectItem value="Μονομελές">Μονομελές</SelectItem><SelectItem value="Πολυμελές">Πολυμελές</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Έτος ΓΑΚ (προαιρετικό)</Label>
            <Input value={year} onChange={(e) => setYear(e.target.value)} placeholder="π.χ. 2026" className="w-28 h-9" data-testid="solon-year" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Υποθέσεις ανά εκτέλεση (έως 1000)</Label>
            <Input type="number" min={1} max={1000} value={limit} onChange={(e) => setLimit(e.target.value)} className="w-32 h-9" data-testid="solon-limit" />
          </div>
          <label className="flex items-center gap-2 text-xs pb-2"><input type="checkbox" checked={onlyUnchecked} onChange={(e) => setOnlyUnchecked(e.target.checked)} data-testid="solon-only-unchecked" />Μόνο όσες δεν έχουν ελεγχθεί</label>
          {running ? (
            <Button variant="destructive" size="sm" onClick={cancel} data-testid="solon-bulk-cancel"><Square className="w-4 h-4 mr-1" />Διακοπή</Button>
          ) : (
            <Button size="sm" onClick={start} disabled={busy} data-testid="solon-bulk-start"><Play className="w-4 h-4 mr-1" />Έλεγχος όλων στο SOLON.GOV</Button>
          )}
          <Button size="sm" variant="ghost" onClick={loadStatus} data-testid="solon-refresh"><RefreshCw className="w-4 h-4" /></Button>
        </div>

        {/* Αυτόματη συνέχιση στο παρασκήνιο (iter109) */}
        <div className={`rounded-lg border p-3 space-y-2 ${auto?.enabled ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200"}`} data-testid="solon-auto-continue">
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input type="checkbox" checked={!!auto?.enabled} onChange={(e) => saveAuto(e.target.checked)} disabled={busy} data-testid="solon-auto-toggle" />
              Αυτόματη επανεκκίνηση στο παρασκήνιο μέχρι να ελεγχθούν όλες οι υποθέσεις
            </label>
            {auto?.enabled && <span className="text-xs text-emerald-700" data-testid="solon-auto-status">ΕΝΕΡΓΗ · απομένουν <b>{auto.remaining ?? "…"}</b> χωρίς έλεγχο · παρτίδες: {auto.batches || 0}{auto.applied_total ? ` · ενημερώθηκαν ${auto.applied_total}` : ""}{auto.last_batch_at ? ` · τελευταία: ${new Date(auto.last_batch_at).toLocaleString("el-GR")}` : ""}</span>}
            {auto?.last_error && <span className="text-xs text-amber-700" data-testid="solon-auto-error">{auto.last_error}</span>}
            <div className="flex items-center gap-1 text-xs ml-auto">
              <span>Παύση μεταξύ παρτίδων (δευτ.)</span>
              <Input type="number" min={5} max={3600} value={autoPause} onChange={(e) => { setAutoPause(e.target.value); setAutoTouched(true); }} className="w-20 h-8" data-testid="solon-auto-pause" />
            </div>
          </div>
          <div className="text-xs text-slate-600">Κάθε παρτίδα = «Υποθέσεις ανά εκτέλεση» με τα παραπάνω φίλτρα (μόνο μη ελεγμένες). Συνεχίζει αυτόματα και μετά από επανεκκίνηση του συστήματος· σε αποτυχία σύνδεσης με το SOLON δοκιμάζει ξανά σε 15′. Προαιρετικά, μετά από κάθε παρτίδα ενημερώνονται αυτόματα τα πεδία:</div>
          <div className="grid sm:grid-cols-3 gap-1">
            {Object.entries(FIELD_LABELS).map(([f, l]) => (
              <label key={f} className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={!!autoFields[f]} onChange={(e) => { setAutoFields({ ...autoFields, [f]: e.target.checked }); setAutoTouched(true); }} data-testid={`solon-auto-field-${f}`} />{l}
              </label>
            ))}
          </div>
          {autoTouched && <Button size="sm" variant="outline" onClick={() => saveAuto(!!auto?.enabled)} disabled={busy} data-testid="solon-auto-save"><Save className="w-4 h-4 mr-1" />Αποθήκευση ρυθμίσεων αυτόματης συνέχισης</Button>}
        </div>

        {job && (
          <div className="rounded-lg border p-3 space-y-2" data-testid="solon-job-status">
            <div className="flex justify-between text-xs text-slate-600">
              <span>Τελευταίος έλεγχος: <b>{{ queued: "σε αναμονή", running: "σε εξέλιξη", completed: "ολοκληρώθηκε", cancelled: "διακόπηκε", interrupted: "διακόπηκε (επανεκκίνηση συστήματος)" }[job.status] || job.status}</b>{job.auto ? " (αυτόματη παρτίδα)" : ""}{job.court_type ? ` · ${job.court_type}` : " · όλα"}{job.gak_year ? ` · ${job.gak_year}` : ""}{job.current && running ? ` · τρέχουσα: ΓΑΚ ${job.current}` : ""}</span>
              <span>{job.done}/{job.total}{job.limit ? ` (όριο ${job.limit})` : ""} · βρέθηκαν {job.found} · σφάλματα {job.errors}</span>
            </div>
            <Progress value={pct} className="h-2" />
          </div>
        )}

        {summary && (
          <div className="rounded-lg border p-3 space-y-3" data-testid="solon-summary">
            <div className="text-xs text-slate-600">Υποθέσεις: <b>{summary.total}</b> · ελεγμένες: <b>{summary.checked}</b> · βρέθηκαν στο SOLON: <b>{summary.found}</b></div>
            <div className="text-sm font-medium">Ενημέρωση Στοιχείων Υποθέσεων — επιλέξτε πεδία (σε παρένθεση: υποθέσεις με διαφορά)</div>
            <div className="grid sm:grid-cols-3 gap-2">
              {Object.entries(FIELD_LABELS).map(([f, l]) => (
                <label key={f} className={`flex items-center gap-2 text-xs ${changed[f] ? "" : "text-slate-400"}`}>
                  <input type="checkbox" disabled={!changed[f]} checked={!!fields[f]} onChange={(e) => setFields({ ...fields, [f]: e.target.checked })} data-testid={`solon-bulk-field-${f}`} />
                  {l} ({changed[f] || 0})
                </label>
              ))}
            </div>
            <Button size="sm" onClick={applyAll} disabled={busy || running} data-testid="solon-bulk-apply">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Ενημέρωση Στοιχείων Υποθέσεων (μαζικά)
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SolonBulkCard;
